import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { tenantApi } from '../../services/api';
import TenantExecutiveFindingDetails from '../../components/findings/TenantExecutiveFindingDetails';
import EvidenceModal from '../../components/msp/EvidenceModal';
import TenantHeader from '../../components/msp/TenantHeader';
import './Msp.css';

const normalizeList = (payload, key) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.[key])) return payload[key];
  return [];
};

function TenantFindings() {
  const { tenantId } = useParams();
  const [findings, setFindings] = useState([]);
  const [status, setStatus] = useState('loading');
  const [scanType, setScanType] = useState('external');
  const [filters, setFilters] = useState({
    severity: '',
    status: '',
    category: '',
    changedSinceLastScan: false,
  });
  const [selectedEvidence, setSelectedEvidence] = useState(null);
  const [selectedFindingIds, setSelectedFindingIds] = useState([]);
  const [bulkStatus, setBulkStatus] = useState('open');
  const [bulkActionStatus, setBulkActionStatus] = useState('idle');

  const loadFindings = useCallback(() => {
    setStatus('loading');
    return tenantApi
      .listFindings(tenantId, {
        scanType,
        severity: filters.severity || undefined,
        status: filters.status || undefined,
        category: filters.category || undefined,
        changedSinceLastScan: filters.changedSinceLastScan || undefined,
      })
      .then((response) => {
        setFindings(normalizeList(response.data, 'findings'));
        setStatus('ready');
      })
      .catch(() => {
        setStatus('error');
      });
  }, [
    tenantId,
    scanType,
    filters.severity,
    filters.status,
    filters.category,
    filters.changedSinceLastScan,
  ]);

  useEffect(() => {
    loadFindings();
  }, [loadFindings]);

  const sortedFindings = useMemo(() => {
    return [...findings].sort((a, b) => {
      const aRank = a.priorityRank || a.priority_rank || 9999;
      const bRank = b.priorityRank || b.priority_rank || 9999;
      return aRank - bRank;
    });
  }, [findings]);

  const handleStatusChange = async (findingId, nextStatus) => {
    try {
      await tenantApi.updateFindingStatus(tenantId, findingId, nextStatus);
      loadFindings();
    } catch (error) {
      console.error('Unable to update finding status', error);
      alert('Unable to update finding status.');
    }
  };

  const toggleFindingSelection = (findingId) => {
    setSelectedFindingIds((prev) =>
      prev.includes(findingId) ? prev.filter((id) => id !== findingId) : [...prev, findingId]
    );
  };

  const handleBulkStatusUpdate = async () => {
    if (selectedFindingIds.length === 0) return;
    setBulkActionStatus('submitting');
    try {
      await tenantApi.bulkUpdateFindings(tenantId, {
        finding_ids: selectedFindingIds,
        status: bulkStatus,
      });
      setBulkActionStatus('success');
      setSelectedFindingIds([]);
      loadFindings();
    } catch (error) {
      console.error('Unable to bulk update findings', error);
      setBulkActionStatus('error');
    }
  };

  const handleAssign = async (findingId, assignee) => {
    try {
      await tenantApi.assignFinding(tenantId, findingId, { assignee });
      loadFindings();
    } catch (error) {
      console.error('Unable to assign finding', error);
    }
  };

  const getRemediationGuidance = (finding) => {
    if (finding.remediation) return finding.remediation;
    const category = (finding.category || '').toLowerCase();
    if (category.includes('tls') || category.includes('certificate')) {
      return 'Update certificates and enforce modern TLS policies.';
    }
    if (category.includes('exposed') || category.includes('port')) {
      return 'Restrict exposure to required IP ranges and close unused ports.';
    }
    if (category.includes('software')) {
      return 'Patch or upgrade the affected service to the latest secure version.';
    }
    return 'Coordinate remediation with the IT owner and document progress.';
  };

  const getDaysOpen = (finding) => {
    const firstSeen = finding.first_seen || finding.firstSeen;
    if (!firstSeen) return '—';
    const days = Math.floor((Date.now() - new Date(firstSeen).getTime()) / (1000 * 60 * 60 * 24));
    return days;
  };

  return (
    <div className="msp-page">
      <TenantHeader
        tenantId={tenantId}
        tenantName="Findings"
        subtitle="Prioritized exposure insights in plain language"
      />

      <div className="msp-toolbar">
        <div className="msp-toolbar-group">
          <div className="msp-tab-toggle" style={{ display: 'flex', background: 'rgba(255,255,255,0.05)', borderRadius: '6px', padding: '2px', marginRight: '16px' }}>
            <button 
              className={`msp-toggle-btn ${scanType === 'external' ? 'active' : ''}`}
              onClick={() => setScanType('external')}
              style={{ padding: '6px 12px', borderRadius: '4px', border: 'none', background: scanType === 'external' ? 'var(--neon-blue)' : 'transparent', color: scanType === 'external' ? '#0d1117' : 'var(--text-secondary)', cursor: 'pointer', fontWeight: 'bold', fontSize: '13px' }}
            >
              External Discovery
            </button>
            <button 
              className={`msp-toggle-btn ${scanType === 'internal' ? 'active' : ''}`}
              onClick={() => setScanType('internal')}
              style={{ padding: '6px 12px', borderRadius: '4px', border: 'none', background: scanType === 'internal' ? 'var(--neon-blue)' : 'transparent', color: scanType === 'internal' ? '#0d1117' : 'var(--text-secondary)', cursor: 'pointer', fontWeight: 'bold', fontSize: '13px' }}
            >
              Internal Application
            </button>
          </div>
        </div>

        <div className="msp-toolbar-group">
          <select
            className="msp-input"
            value={bulkStatus}
            onChange={(event) => setBulkStatus(event.target.value)}
          >
            <option value="open">Bulk Status: Open</option>
            <option value="fixed">Bulk Status: Fixed</option>
            <option value="accepted_risk">Bulk Status: Accepted Risk</option>
          </select>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleBulkStatusUpdate}
            disabled={bulkActionStatus === 'submitting' || selectedFindingIds.length === 0}
          >
            {bulkActionStatus === 'submitting' ? 'Updating…' : 'Apply Bulk Status'}
          </button>
        </div>
        <select
          className="msp-input"
          value={filters.severity}
          onChange={(event) => setFilters({ ...filters, severity: event.target.value })}
        >
          <option value="">All severities</option>
          <option value="critical">Critical</option>
          <option value="high">High</option>
          <option value="medium">Medium</option>
          <option value="low">Low</option>
        </select>
        <select
          className="msp-input"
          value={filters.status}
          onChange={(event) => setFilters({ ...filters, status: event.target.value })}
        >
          <option value="">All statuses</option>
          <option value="open">Open</option>
          <option value="fixed">Fixed</option>
          <option value="accepted_risk">Accepted Risk</option>
        </select>
        <input
          type="text"
          className="msp-input"
          placeholder="Category"
          value={filters.category}
          onChange={(event) => setFilters({ ...filters, category: event.target.value })}
        />
        <label className="msp-checkbox">
          <input
            type="checkbox"
            checked={filters.changedSinceLastScan}
            onChange={(event) => setFilters({ ...filters, changedSinceLastScan: event.target.checked })}
          />
          Changed since last scan
        </label>
      </div>

      {status === 'loading' && <div className="msp-empty">Loading findings…</div>}
      {status === 'error' && <div className="msp-empty">Unable to load findings.</div>}
      {status === 'ready' && findings.length === 0 && (
        <div className="msp-empty">No findings match the selected filters.</div>
      )}

      {status === 'ready' && findings.length > 0 && (
        <table className="msp-table">
          <thead>
            <tr>
              <th></th>
              <th>Priority</th>
              <th>Finding</th>
              <th>Severity</th>
              <th>Urgency</th>
              <th>Category</th>
              <th>First Seen</th>
              <th>Last Seen</th>
              <th>Days Open</th>
              <th>Assignment</th>
              <th>Guidance</th>
               <th>Compliance</th>
               <th>Scan</th>
              <th>Status</th>
              <th>Evidence</th>
            </tr>
          </thead>
          <tbody>
            {sortedFindings.map((finding) => (
              <tr key={finding.id || finding.finding_id}>
                <td>
                  <input
                    type="checkbox"
                    checked={selectedFindingIds.includes(finding.id || finding.finding_id)}
                    onChange={() => toggleFindingSelection(finding.id || finding.finding_id)}
                    aria-label={`Select ${finding.title || finding.name}`}
                  />
                </td>
                <td>{finding.priorityRank || finding.priority_rank || '—'}</td>
                <td>
                  <div>{finding.title || finding.name}</div>
                  <TenantExecutiveFindingDetails finding={finding} />
                </td>
                <td>{finding.severity}</td>
                <td>{finding.urgency || '—'}</td>
                <td>{finding.category || 'General'}</td>
                <td>{finding.first_seen || finding.firstSeen || '—'}</td>
                <td>{finding.last_seen || finding.lastSeen || '—'}</td>
                <td>{getDaysOpen(finding)}</td>
                <td>
                  <select
                    className="msp-input"
                    value={finding.assignee || finding.owner || ''}
                    onChange={(event) => handleAssign(finding.id || finding.finding_id, event.target.value)}
                  >
                    <option value="">Unassigned</option>
                    <option value="msp-analyst">MSP Analyst</option>
                    <option value="tenant-it">Tenant IT</option>
                    <option value="vendor">Vendor</option>
                  </select>
                </td>
                <td className="msp-small">{getRemediationGuidance(finding)}</td>
                <td className="msp-small">
                  {(finding.compliance_refs || []).length > 0
                    ? (finding.compliance_refs || []).join(', ')
                    : '—'}
                </td>
                <td>
                  {finding.scan_id ? (
                    <a
                      href={scanType === 'internal' ? `/scans/${finding.scan_id}` : `/msp/tenants/${tenantId}/external-scans/${finding.scan_id}`}
                      className="msp-link"
                    >
                      View Scan
                    </a>
                  ) : (
                    '—'
                  )}
                </td>
                <td>
                  <select
                    className="msp-input"
                    value={finding.status || 'open'}
                    onChange={(event) => handleStatusChange(finding.id || finding.finding_id, event.target.value)}
                  >
                    <option value="open">Open</option>
                    <option value="fixed">Fixed</option>
                    <option value="accepted_risk">Accepted Risk</option>
                  </select>
                </td>
                <td>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setSelectedEvidence(finding.evidence || finding)}
                  >
                    View
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <EvidenceModal
        isOpen={!!selectedEvidence}
        onClose={() => setSelectedEvidence(null)}
        evidence={selectedEvidence}
      />
    </div>
  );
}

export default TenantFindings;
